import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload as UploadIcon, 
  FileText, 
  Download, 
  ChevronRight, 
  Check,
  AlertCircle,
  Loader2,
  Package,
  Briefcase
} from 'lucide-react';
import { cn } from '../lib/utils';
import { collection, addDoc, serverTimestamp } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import Papa from 'papaparse';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

interface ItemImportProps {
  onClose: () => void;
  onComplete: () => void;
  orgId: string;
}

export default function ItemImport({ onClose, onComplete, orgId }: ItemImportProps) {
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState<'product' | 'service' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mappedData, setMappedData] = useState<any[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file || !importType) return;
    setIsUploading(true);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setIsUploading(false);
        const { data } = results;
        // Basic validation and mapping
        const validData = (data as any[]).filter(row => (row.SKU || row.Code) && (row.Name));
        setMappedData(validData);
        setStep(3);
      },
      error: (error) => {
        setIsUploading(false);
        notify.error("Failed to parse CSV file");
        console.error("CSV Parse Error:", error);
      }
    });
  };

  const handleDownloadTemplate = () => {
    const headers = ['SKU', 'Name', 'Description', 'UnitPrice', 'CostPrice', 'Category', 'TaxRate'];
    if (importType === 'product') {
      headers.push('Quantity', 'TrackInventory');
    }
    const csvContent = [
      headers.join(','),
      `SKU-001,Sample ${importType === 'product' ? 'Product' : 'Service'},A sample description,100,50,General,0%${importType === 'product' ? ',10,true' : ''}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${importType}_import_template.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFinalImport = async () => {
    setIsImporting(true);
    try {
      let importedCount = 0;
      for (const row of mappedData) {
        const itemData = {
          code: row.SKU || row.Code,
          name: row.Name,
          description: row.Description || '',
          unitPrice: parseFloat(row.UnitPrice) || 0,
          costPrice: parseFloat(row.CostPrice) || 0,
          category: row.Category || '',
          taxRate: row.TaxRate || '0%',
          type: importType,
          status: 'active',
          quantity: importType === 'product' ? (parseFloat(row.Quantity) || 0) : 0,
          trackInventory: importType === 'product' ? (row.TrackInventory?.toLowerCase() === 'true') : false,
          orgId,
          createdBy: auth.currentUser?.uid || '',
          createdAt: serverTimestamp(),
          actionLog: [{
            action: 'Imported',
            by: auth.currentUser?.displayName || 'System',
            byEmail: auth.currentUser?.email || 'System',
            at: new Date()
          }]
        };

        try {
          if (isLocalSession()) {
            const { orgId: _legacyOrgId, createdBy: _legacyCreatedBy, createdAt: _legacyCreatedAt, ...localItemData } = itemData;
            await localApiRequest(`/api/organizations/${orgId}/items`, {
              method: 'POST',
              body: JSON.stringify(localItemData)
            });
          } else {
            await addDoc(collection(db, 'items'), itemData);
          }
          importedCount++;
        } catch (error) {
          if (!isLocalSession()) handleFirestoreError(error, OperationType.CREATE, 'items');
          throw error;
        }
      }
      notify.confirm(`Successfully imported ${importedCount} ${importType}s`);
      onComplete();
    } catch (error) {
      console.error("Import error:", error);
      notify.error(error instanceof Error ? error.message : 'Failed to import items');
    } finally {
      setIsImporting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-4 mb-12">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
              step === s ? "bg-[#141414] text-[#E4E3E0]" : 
              step > s ? "bg-green-500 text-white" : "bg-[#141414]/10 text-[#141414]/40"
            )}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={cn(
              "text-sm font-bold",
              step === s ? "opacity-100" : "opacity-40"
            )}>
              {s === 1 ? 'Type' : s === 2 ? 'Upload' : 'Review'}
            </span>
          </div>
          {s < 3 && <div className="w-12 h-[2px] bg-[#141414]/10" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
      <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-[40px] p-12 shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center">
                <h2 className="text-4xl font-bold tracking-tight mb-4">What would you like to import?</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl mx-auto">
                  Select the type of items you want to bring into the system.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button 
                  onClick={() => {
                    setImportType('product');
                    setStep(2);
                  }}
                  className="bg-white border border-[#141414] p-8 rounded-[32px] hover:shadow-xl transition-all group text-left"
                >
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Package className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Import Products</h3>
                  <p className="text-sm opacity-50 italic serif">Import your physical goods and inventory items</p>
                </button>

                <button 
                  onClick={() => {
                    setImportType('service');
                    setStep(2);
                  }}
                  className="bg-white border border-[#141414] p-8 rounded-[32px] hover:shadow-xl transition-all group text-left"
                >
                  <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Briefcase className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Import Services</h3>
                  <p className="text-sm opacity-50 italic serif">Import your professional services and non-inventory items</p>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Upload {importType === 'product' ? 'Products' : 'Services'}</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl">
                  Upload a CSV file containing your {importType} records.
                </p>
              </div>

              <div className="flex justify-center">
                <button 
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 border border-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold"
                >
                  <Download className="w-4 h-4" />
                  <span>Download {importType === 'product' ? 'Product' : 'Service'} Template</span>
                </button>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">File to upload</h3>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed border-[#141414]/20 rounded-3xl p-12 text-center cursor-pointer hover:border-[#141414] hover:bg-white/20 transition-all group relative",
                    file && "border-green-500 bg-green-50/50"
                  )}
                >
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden" 
                    accept=".csv"
                    onChange={handleFileSelect}
                  />
                  {!file ? (
                    <>
                      <div className="w-16 h-16 bg-[#141414]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <UploadIcon className="w-8 h-8 opacity-40" />
                      </div>
                      <p className="text-xl font-bold italic serif mb-2">Drag and drop CSV file or select manually</p>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="bg-[#141414] text-[#E4E3E0] px-8 py-3 rounded-full font-bold mt-4 hover:opacity-90 transition-opacity relative z-10"
                      >
                        Select file
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-4">
                      <div className="p-4 bg-green-100 text-green-600 rounded-2xl">
                        <FileText className="w-8 h-8" />
                      </div>
                      <div className="text-left">
                        <p className="text-lg font-bold">{file.name}</p>
                        <p className="text-sm opacity-50">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="ml-4 p-2 hover:bg-red-100 text-red-500 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <button 
                  onClick={() => setStep(1)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Back
                </button>
                <button 
                  disabled={!file || isUploading}
                  onClick={handleUpload}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center gap-2"
                >
                  {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Review {importType === 'product' ? 'Products' : 'Services'}</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed">
                  We found {mappedData.length} {importType}s in your file.
                </p>
              </div>

              <div className="bg-white/30 border border-[#141414] rounded-3xl overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#141414] text-[#E4E3E0] uppercase text-[10px] tracking-widest font-bold sticky top-0 z-10">
                      <th className="px-6 py-4">SKU</th>
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Category</th>
                      <th className="px-6 py-4 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#141414]/10">
                    {mappedData.map((item, i) => (
                      <tr key={i} className="hover:bg-white/20 transition-colors">
                        <td className="px-6 py-4 text-sm font-mono">{item.SKU || item.Code}</td>
                        <td className="px-6 py-4 text-sm font-bold">{item.Name}</td>
                        <td className="px-6 py-4 text-sm">{item.Category}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold font-mono">
                          ${parseFloat(item.UnitPrice).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-4">
                <button 
                  onClick={() => setStep(2)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Back
                </button>
                <button 
                  disabled={isImporting}
                  onClick={handleFinalImport}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isImporting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Import'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
